import React, { useEffect, useRef, useState } from "react";
import { Lock, Loader2, MailCheck, MailX, RefreshCw, Trash2, User } from "lucide-react";
import { ParallaxBackground } from "@/components/site/ParallaxBackground";

const API = process.env.REACT_APP_BACKEND_URL;

const REG_DETAIL_OPTIONS = {
  gender: ["Male", "Female"],
  nationality: ["Malaysian", "Others"],
  jersey: ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
  jerseyKids: ["XS", "S", "M"],
  ageAmateur: ["U8 (6-7 y/o)", "U10 (8-9 y/o)", "U12 (10-11 y/o)", "U14 (12-13 y/o)", "U16 (14-15 y/o)", "U18 (16-17 y/o)", "SENIOR (18-40 y/o)"],
  ageKids: Array.from({ length: 8 }, (_, i) => `${4 + i} y/o`),
  weight: [...Array.from({ length: 35 }, (_, i) => `${20 + i * 2}KG`), "90KG++"],
};

// [field key, label, option-set/"number"/"date"] per registration type
const REG_DETAIL_FIELDS = {
  amateur: [
    ["full_name", "Full Name"], ["nric_passport", "NRIC/Passport"], ["gender", "Gender", "gender"],
    ["nationality", "Nationality", "nationality"], ["age_category", "Age Category", "ageAmateur"],
    ["weight_class", "Weight Class", "weight"], ["height_cm", "Height (CM)", "number"],
    ["jersey_size", "Jersey Size", "jersey"], ["club_gym", "Club/Gym"], ["coach_name", "Coach Name"],
    ["coach_phone", "Coach Phone"], ["fight_record", "Fight Record"],
  ],
  seminar_adult: [
    ["full_name", "Full Name"], ["nric_passport", "NRIC/Passport"], ["gender", "Gender", "gender"],
    ["jersey_size", "Jersey Size", "jersey"], ["contact_number", "Contact Number"],
  ],
  seminar_kids: [
    ["full_name", "Full Name"], ["nric_passport", "NRIC/Passport"], ["gender", "Gender", "gender"],
    ["jersey_size", "Jersey Size", "jerseyKids"], ["date_of_birth", "Date of Birth", "date"],
    ["age_category", "Age Category", "ageKids"], ["guardian_name", "Parent/Guardian Name"], ["contact_number", "Guardian Contact"],
  ],
};

const REG_FIELD_LABELS = {
  full_name: "Full Name", nric_passport: "NRIC/Passport", gender: "Gender", nationality: "Nationality",
  age_category: "Age Category", date_of_birth: "Date of Birth", guardian_name: "Parent/Guardian",
  weight_class: "Weight Class", height_cm: "Height (CM)", jersey_size: "Jersey Size", club_gym: "Club/Gym",
  coach_name: "Manager/Coach", coach_phone: "Coach Phone", fight_record: "Fight Record",
  contact_number: "Contact Number", email: "Email", photo: "Profile Photo",
};

const BULK_OPTION_SETS = {
  weight: REG_DETAIL_OPTIONS.weight,
  ageAll: [...REG_DETAIL_OPTIONS.ageAmateur, ...REG_DETAIL_OPTIONS.ageKids],
  jersey: REG_DETAIL_OPTIONS.jersey,
  gender: REG_DETAIL_OPTIONS.gender,
  nationality: REG_DETAIL_OPTIONS.nationality,
};

// [field key, label, bulk-option-set/"text"]
const BULK_FIELDS = [
  ["weight_class", "Weight Class", "weight"],
  ["age_category", "Age Category", "ageAll"],
  ["jersey_size", "Jersey Size", "jersey"],
  ["gender", "Gender", "gender"],
  ["nationality", "Nationality", "nationality"],
  ["club_gym", "Club/Gym", "text"],
  ["coach_name", "Manager/Coach", "text"],
  ["coach_phone", "Coach Phone", "text"],
  ["fight_record", "Fight Record", "text"],
  ["guardian_name", "Parent/Guardian (Kids)", "text"],
];

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("pahlawan_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tracking, setTracking] = useState({});
  const [emailEdit, setEmailEdit] = useState(null);
  const [detailsEdit, setDetailsEdit] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [selectedRegs, setSelectedRegs] = useState([]);
  const [bulkField, setBulkField] = useState("weight_class");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pendingRegs, setPendingRegs] = useState(null); // null = main (paid) list, array = unpaid checkouts view
  const [photoBusy, setPhotoBusy] = useState(null);
  const photoTarget = useRef(null);
  const photoInputRef = useRef(null);
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [regs, setRegs] = useState([]);
  const [regStats, setRegStats] = useState(null);
  const [weighin, setWeighin] = useState({ weighin_date: "", reminder_days: 3, last_reminder_at: "", last_reminder_sent: 0 });
  const [seminar, setSeminar] = useState({ weighin_date: "", reminder_days: 3, last_reminder_at: "", last_reminder_sent: 0 });
  const [prices, setPrices] = useState({ amateur: "89.00", seminar_adult: "169.00", seminar_kids: "119.00" });
  const [regQuery, setRegQuery] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Login failed");
      localStorage.setItem("pahlawan_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async (t) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/orders`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.status === 401) {
        localStorage.removeItem("pahlawan_admin_token");
        setToken("");
        return;
      }
      const data = await res.json();
      setOrders(data.orders || []);
      const [s, pr, rg, st, se, ss, pz] = await Promise.all([
        fetch(`${API}/api/admin/summary`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()).catch(() => null),
        fetch(`${API}/api/admin/products`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()).catch(() => ({ products: [] })),
        fetch(`${API}/api/admin/registrations`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()).catch(() => ({ registrations: [] })),
        fetch(`${API}/api/admin/registrations/stats`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()).catch(() => null),
        fetch(`${API}/api/admin/settings`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()).catch(() => null),
        fetch(`${API}/api/admin/settings?scope=seminar`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()).catch(() => null),
        fetch(`${API}/api/admin/prices`, { headers: { Authorization: `Bearer ${t}` } }).then((r) => r.json()).catch(() => null),
      ]);
      if (s) setSummary(s);
      setProducts(pr.products || []);
      setRegs(rg.registrations || []);
      if (st) setRegStats(st);
      if (se) setWeighin({
        weighin_date: se.weighin_date || "",
        reminder_days: se.reminder_days || 3,
        last_reminder_at: se.last_reminder_at || "",
        last_reminder_sent: se.last_reminder_sent || 0,
      });
      if (ss) setSeminar({
        weighin_date: ss.weighin_date || "",
        reminder_days: ss.reminder_days || 3,
        last_reminder_at: ss.last_reminder_at || "",
        last_reminder_sent: ss.last_reminder_sent || 0,
      });
      if (pz) setPrices({
        amateur: pz.amateur || "89.00",
        seminar_adult: pz.seminar_adult || "169.00",
        seminar_kids: pz.seminar_kids || "119.00",
      });
    } finally {
      setLoading(false);
    }
  };

  const patchProduct = (id, field, value) =>
    setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

  const patchStock = (id, sz, value) =>
    setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, stock: { ...p.stock, [sz]: Number(value) } } : p)));

  const saveProduct = async (p) => {
    const body = {
      id: p.id, name: p.name, price_value: Number(p.price_value), category: p.category || "Merchandise",
      image: p.image || "", images: p.images || [], desc: p.desc || "", variants: p.variants || "", sizeChart: p.sizeChart || null,
      sizes: p.sizes || [], stock: p.stock || {}, track: !!p.track, published: !!p.published, sort: p.sort || 99,
      badge: p.badge || null, details: p.details || null,
    };
    try {
      const res = await fetch(`${API}/api/admin/products`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(res.status === 401 ? "Session expired — please log in again" : (d.detail ? JSON.stringify(d.detail) : `Save failed (${res.status})`));
      }
    } catch (err) {
      window.alert(err.message);
      return;
    }
    loadOrders(token);
  };

  const delProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    const res = await fetch(`${API}/api/admin/products/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      window.alert(res.status === 401 ? "Session expired — please log in again" : `Delete failed (${res.status})`);
      return;
    }
    loadOrders(token);
  };

  const addProduct = () => {
    const id = (window.prompt("New product ID (lowercase, no spaces, e.g. combo-ticket):") || "").trim();
    if (!id) return;
    setProducts((ps) => [...ps, { id, name: "New Product", price_value: 0, category: "Merchandise", image: "", desc: "", sizes: [], stock: {}, track: false, published: false, sort: 99 }]);
  };

  const saveSettings = async () => {
    if (!weighin.weighin_date) {
      window.alert("Pick a weigh-in date first");
      return;
    }
    const res = await fetch(`${API}/api/admin/settings`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ weighin_date: weighin.weighin_date, reminder_days: Number(weighin.reminder_days) || 3 }),
    });
    if (!res.ok) {
      window.alert("Failed to save schedule");
      return;
    }
    loadOrders(token);
  };

  const remindNow = async () => {
    const paidCount = regStats?.amateur_paid ?? regStats?.paid ?? 0;
    if (!window.confirm(`Send weigh-in reminder emails to ${paidCount} paid amateur fighter(s) now?`)) return;
    const res = await fetch(`${API}/api/admin/registrations/remind`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Failed to send reminders");
      return;
    }
    window.alert(`Reminders sent: ${d.sent}/${d.total_paid}`);
    loadOrders(token);
  };

  const saveSeminarSettings = async () => {
    if (!seminar.weighin_date) {
      window.alert("Pick a seminar date first");
      return;
    }
    const res = await fetch(`${API}/api/admin/settings?scope=seminar`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ weighin_date: seminar.weighin_date, reminder_days: Number(seminar.reminder_days) || 3 }),
    });
    if (!res.ok) {
      window.alert("Failed to save schedule");
      return;
    }
    loadOrders(token);
  };

  const remindSeminarNow = async () => {
    const paidCount = (regStats?.by_type?.seminar_adult ?? 0) + (regStats?.by_type?.seminar_kids ?? 0);
    if (!window.confirm(`Send seminar reminder emails to ${paidCount} paid seminar participant(s) now?`)) return;
    const res = await fetch(`${API}/api/admin/registrations/remind-seminar`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Failed to send reminders");
      return;
    }
    window.alert(`Seminar reminders sent: ${d.sent}/${d.total_paid}`);
    loadOrders(token);
  };

  const resendEmail = async (r) => {
    const res = await fetch(`${API}/api/admin/registrations/${r.id}/resend-email`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(d.detail || "Resend failed — try again");
      return;
    }
    window.alert(d.email_status === "sent"
      ? `Email re-sent to ${r.email}.`
      : `Email to ${r.email} is still failing — the address may be unreachable. Contact the client via WhatsApp instead.`);
    loadOrders(token);
  };

  const saveEmail = async (r) => {
    const newEmail = (emailEdit?.value || "").trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newEmail)) {
      window.alert("Please enter a valid email address");
      return;
    }
    const res = await fetch(`${API}/api/admin/registrations/${r.id}/email`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ email: newEmail }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Failed to update email");
      return;
    }
    window.alert(d.email_status === "sent"
      ? `Email updated — confirmation sent to ${newEmail}.`
      : `Email updated to ${newEmail}, but sending still failed — double-check the address or contact the client via WhatsApp.`);
    setEmailEdit(null);
    loadOrders(token);
  };

  const openDetailsEdit = (r) => {
    if (detailsEdit?.id === r.id) {
      setDetailsEdit(null);
      return;
    }
    const form = {};
    (REG_DETAIL_FIELDS[r.reg_type] || REG_DETAIL_FIELDS.amateur).forEach(([key]) => {
      form[key] = r[key] == null ? "" : String(r[key]);
    });
    setDetailsEdit({ id: r.id, form });
  };

  const saveDetails = async (r) => {
    const form = detailsEdit?.form || {};
    const changes = {};
    (REG_DETAIL_FIELDS[r.reg_type] || REG_DETAIL_FIELDS.amateur).forEach(([key]) => {
      const orig = r[key] == null ? "" : String(r[key]);
      const val = (form[key] || "").trim();
      if (val && val !== orig) changes[key] = key === "height_cm" ? parseInt(val, 10) : val;
    });
    if (Object.keys(changes).length === 0) {
      window.alert("No changes to save");
      return;
    }
    const res = await fetch(`${API}/api/admin/registrations/${r.id}/details`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(changes),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Failed to update details");
      return;
    }
    window.alert(`Details updated: ${(d.updated_fields || []).join(", ")}${d.email_notified === "sent" ? " — participant notified by email" : ""}`);
    setDetailsEdit(null);
    loadOrders(token);
  };

  const toggleRegSelect = (id) => {
    setSelectedRegs((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const applyBulk = async () => {
    if (!selectedRegs.length || !bulkField || !bulkValue) {
      window.alert("Select registrations, a field, and a value first");
      return;
    }
    const label = BULK_FIELDS.find(([k]) => k === bulkField)?.[1] || bulkField;
    if (!window.confirm(`Update ${label} to "${bulkValue}" for ${selectedRegs.length} registration(s)? Participants will be notified by email.`)) return;
    setBulkBusy(true);
    const res = await fetch(`${API}/api/admin/registrations/bulk-update`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ reg_ids: selectedRegs, field: bulkField, value: bulkValue }),
    });
    const d = await res.json().catch(() => ({}));
    setBulkBusy(false);
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Bulk update failed");
      return;
    }
    const failTxt = (d.failed || []).map((f) => `${f.id}: ${f.reason}`).join("\n");
    window.alert(`Bulk update done — ${d.updated} updated${d.failed?.length ? `, ${d.failed.length} skipped:\n${failTxt}` : ""}`);
    setSelectedRegs([]);
    setBulkValue("");
    loadOrders(token);
  };


  const syncPayment = async (r) => {
    const res = await fetch(`${API}/api/admin/registrations/${r.id}/sync-payment`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert("Sync failed — try again");
      return;
    }
    window.alert(d.status === "pending_review"
      ? `Payment received for ${r.full_name} — status is now AWAITING APPROVAL and the "payment received" email has been sent. Click Approve Payment to confirm and send the registration number.`
      : d.status === "paid"
        ? `${r.reg_no || r.full_name} is already PAID.`
        : `HitPay still shows this payment as "${d.status}" — the payment has not been completed yet.`);
    loadOrders(token);
    if (pendingRegs !== null) loadPendingRegs();
  };

  const loadPendingRegs = async () => {
    const res = await fetch(`${API}/api/admin/registrations/pending`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert("Failed to load unpaid checkouts");
      return;
    }
    setPendingRegs(d.registrations || []);
  };

  const togglePendingView = async () => {
    if (pendingRegs !== null) {
      setPendingRegs(null);
      return;
    }
    await loadPendingRegs();
  };

  const pickRegPhoto = (regId) => {
    photoTarget.current = regId;
    photoInputRef.current?.click();
  };

  const uploadRegPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const regId = photoTarget.current;
    if (!file || !regId) return;
    setPhotoBusy(regId);
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/api/admin/registrations/${regId}/photo`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const d = await res.json().catch(() => ({}));
    setPhotoBusy(null);
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Photo upload failed");
      return;
    }
    loadOrders(token);
    if (pendingRegs !== null) loadPendingRegs();
  };

  const approvePayment = async (r) => {
    if (!window.confirm(`Approve ${r.full_name} (${r.reg_no || "no reg no"})?\n\nThe client will receive the confirmation email with their registration number.`)) return;
    const res = await fetch(`${API}/api/admin/registrations/${r.id}/approve`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Approve failed");
      return;
    }
    window.alert(`${r.full_name} is now PAID — confirmation email with registration number sent.`);
    loadOrders(token);
  };

  const forceApprove = async (r) => {
    if (!window.confirm(`FORCE APPROVE ${r.full_name} (${r.reg_no || "no reg no"})?\n\nUse this ONLY if you have verified the payment yourself (e.g. in your HitPay dashboard). HitPay sync was not able to confirm this payment automatically.\n\nThe client will be marked PAID and the confirmation email with their registration number will be sent to ${r.email}.`)) return;
    const res = await fetch(`${API}/api/admin/registrations/${r.id}/approve`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(typeof d.detail === "string" ? d.detail : "Approve failed");
      return;
    }
    window.alert(`${r.full_name} is now PAID (manual approval) — confirmation email with registration number sent to ${r.email}.`);
    loadOrders(token);
  };

  const copyPaymentLink = async (r) => {
    const res = await fetch(`${API}/api/admin/registrations/${r.id}/payment-link`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) {
      window.alert(d.detail || "Could not get payment link");
      return;
    }
    try {
      await navigator.clipboard.writeText(d.payment_link);
      window.alert(`Payment link copied — WhatsApp it to ${r.full_name} (${r.email}):\n\n${d.payment_link}\n\nOnce they pay, use Sync Payment here.`);
    } catch {
      window.prompt("Copy this payment link and WhatsApp it to the client:", d.payment_link);
    }
  };

  const deleteOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.order_id} (${o.customer_name})?\n\n${o.status === "paid" ? "This order is PAID — its stock will be returned. " : ""}This cannot be undone.`)) return;
    const res = await fetch(`${API}/api/admin/orders/${o.order_id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      window.alert("Failed to delete order");
      return;
    }
    loadOrders(token);
  };

  const deleteRegistration = async (r) => {
    if (!window.confirm(`Delete registration ${r.full_name} (${r.reg_no || "no reg no"})?\n\nThis cannot be undone.`)) return;
    const res = await fetch(`${API}/api/admin/registrations/${r.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      window.alert("Failed to delete registration");
      return;
    }
    loadOrders(token);
  };

  const savePrices = async () => {    const payload = {
      amateur: Number(prices.amateur),
      seminar_adult: Number(prices.seminar_adult),
      seminar_kids: Number(prices.seminar_kids),
    };
    if (Object.values(payload).some((v) => !(v > 0))) {
      window.alert("All prices must be more than RM0");
      return;
    }
    if (!window.confirm(`Save new prices? Amateur RM${payload.amateur.toFixed(2)} · Adult Seminar RM${payload.seminar_adult.toFixed(2)} · Kids Seminar RM${payload.seminar_kids.toFixed(2)}\n\nNew prices take effect immediately for new checkouts.`)) return;
    const res = await fetch(`${API}/api/admin/prices`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      window.alert("Failed to save prices");
      return;
    }
    window.alert("Prices saved. Registration pages now show the new prices.");
    loadOrders(token);
  };

  useEffect(() => {
    if (token) loadOrders(token);
  }, [token]);

  const ship = async (orderId) => {
    const tn = (tracking[orderId] || "").trim();
    if (tn.length < 3) return;
    try {
      const res = await fetch(`${API}/api/admin/orders/${orderId}/ship`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tracking_number: tn }),
      });
      if (!res.ok) throw new Error();
      loadOrders(token);
    } catch {
      /* ignore */
    }
  };

  if (!token) {
    return (
      <div className="relative flex min-h-screen items-center justify-center px-4 text-white">
        <ParallaxBackground />
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl border border-neon-violet/40 bg-surface p-8 glow-ring" data-testid="admin-login">
          <div className="mb-6 flex items-center gap-2">
            <Lock className="h-5 w-5 text-neon-pink" />
            <h1 className="font-display text-2xl">ADMIN LOGIN</h1>
          </div>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" className="mb-3 w-full rounded-xl border border-white/15 bg-void/60 px-4 py-3 text-sm text-white outline-none focus:border-neon-cyan" data-testid="admin-email" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" className="mb-4 w-full rounded-xl border border-white/15 bg-void/60 px-4 py-3 text-sm text-white outline-none focus:border-neon-cyan" data-testid="admin-password" />
          {error && <p className="mb-3 text-sm text-neon-pink" data-testid="admin-error">{error}</p>}
          <button disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet py-3 font-mono-cap text-[11px] text-white" data-testid="admin-login-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  const paid = orders.filter((o) => o.status === "paid" || o.status === "shipped");
  const q = query.trim().toLowerCase();
  const filtered = orders.filter((o) => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (!q) return true;
    return [o.order_id, o.customer_name, o.customer_email, o.phone, o.tracking_number]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });
  const FILTERS = ["all", "paid", "shipped", "pending"];

  const rq = regQuery.trim().toLowerCase();
  const filteredRegs = regs.filter((r) => {
    if (!rq) return true;
    return [r.reg_no, r.full_name, r.nric_passport, r.club_gym, r.email]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(rq));
  });
  const filteredPending = (pendingRegs || []).filter((r) => {
    if (!rq) return true;
    return [r.reg_no, r.full_name, r.nric_passport, r.club_gym, r.email]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(rq));
  });
  const shownRegs = pendingRegs !== null ? filteredPending : filteredRegs;

  return (
    <div className="relative min-h-screen px-4 py-10 text-white sm:px-8">
      <ParallaxBackground />
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-display text-4xl">MERCH ORDERS</h1>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={uploadRegPhoto}
              data-testid="reg-photo-input"
            />
            <p className="mt-1 font-mono-cap text-[11px] text-neon-cyan">{paid.length} paid · {orders.length} total</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => loadOrders(token)} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono-cap text-[10px] hover:border-neon-cyan" data-testid="admin-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <a
              href={`${API}/api/admin/orders/export?auth=${token}`}
              className="flex items-center rounded-full border border-white/15 px-4 py-2 font-mono-cap text-[10px] hover:border-neon-cyan"
              data-testid="export-orders-btn"
            >
              Export Orders
            </a>
            <button onClick={() => { localStorage.removeItem("pahlawan_admin_token"); setToken(""); }} className="rounded-full border border-white/15 px-4 py-2 font-mono-cap text-[10px] hover:border-neon-pink">Logout</button>
          </div>
        </div>

        {regStats && (
          <div className="mb-6 rounded-2xl border border-white/10 bg-surface/80 p-5" data-testid="reg-stats">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-mono-cap text-[11px] text-neon-cyan">Fighters by Weight Class</p>
              <p className="font-mono-cap text-[10px] text-slate-400" data-testid="reg-stats-total">
                {regStats.paid} paid · {regStats.amateur_paid ?? 0}/{regStats.amateur_limit ?? 400} amateur slots · {regStats.by_type?.seminar_adult ?? 0} seminar adult · {regStats.by_type?.seminar_kids ?? 0} seminar kids
              </p>
            </div>
            {regStats.by_weight.length === 0 ? (
              <p className="mt-3 font-body text-sm text-slate-500">No paid fighters yet — stats fill in automatically as payments land.</p>
            ) : (
              <div className="mt-4 flex flex-wrap gap-2">
                {regStats.by_weight.map((w) => (
                  <div key={w.weight_class} className="flex items-center gap-2 rounded-xl border border-white/10 bg-void/50 px-4 py-2" data-testid={`stat-wc-${w.weight_class}`}>
                    <span className="font-mono-cap text-[9px] text-slate-400">{w.weight_class}</span>
                    <span className="font-display text-lg gradient-text">{w.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-white/10 bg-surface/80 p-5" data-testid="admin-registrations">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <p className="font-mono-cap text-[11px] text-neon-cyan">
              Registrations (Amateur &amp; Seminar) — {regs.filter((r) => r.status === "paid").length} paid · {regs.length} total
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={regQuery}
                onChange={(e) => setRegQuery(e.target.value)}
                placeholder="Search reg no / name / NRIC…"
                className="w-52 rounded-full border border-white/15 bg-void/60 px-4 py-1.5 font-body text-xs text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan"
                data-testid="reg-search"
              />
              <button
                onClick={togglePendingView}
                className={`rounded-full border px-4 py-1.5 font-mono-cap text-[10px] transition-colors ${
                  pendingRegs !== null
                    ? "border-amber-400 bg-amber-400/20 text-amber-300"
                    : "border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
                }`}
                data-testid="toggle-unpaid-btn"
              >
                {pendingRegs !== null ? `Unpaid (${pendingRegs.length}) — Back` : "Unpaid Checkouts"}
              </button>
              <a
                href={`${API}/api/admin/registrations/weigh-in?auth=${token}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-neon-cyan/40 px-4 py-1.5 font-mono-cap text-[10px] hover:bg-neon-cyan/10"
                data-testid="weighin-btn"
              >
                Weigh-In (Amateur)
              </a>
              <a
                href={`${API}/api/admin/registrations/seminar-list?auth=${token}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-neon-pink/40 px-4 py-1.5 font-mono-cap text-[10px] text-neon-pink hover:bg-neon-pink/10"
                data-testid="seminar-list-btn"
              >
                Seminar List
              </a>
              <a
                href={`${API}/api/admin/registrations/export?auth=${token}`}
                className="rounded-full border border-white/15 px-4 py-1.5 font-mono-cap text-[10px] text-slate-300 hover:border-neon-cyan"
                data-testid="export-registrations-btn"
              >
                Export CSV
              </a>
            </div>
          </div>
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-neon-pink/25 bg-void/40 p-4" data-testid="price-settings">
            <p className="w-full font-mono-cap text-[9px] text-neon-pink">Package Prices (RM) — take effect immediately for new checkouts</p>
            {[
              ["amateur", "Amateur"],
              ["seminar_adult", "Adult Seminar"],
              ["seminar_kids", "Kids Seminar"],
            ].map(([key, label]) => (
              <div key={key}>
                <p className="font-mono-cap mb-1 text-[9px] text-slate-400">{label}</p>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={prices[key]}
                  onChange={(e) => setPrices((s) => ({ ...s, [key]: e.target.value }))}
                  className="w-28 rounded-lg border border-white/15 bg-void/60 px-3 py-2 font-body text-sm text-white outline-none"
                  data-testid={`price-${key}-input`}
                />
              </div>
            ))}
            <button onClick={savePrices} className="rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-5 py-2 font-mono-cap text-[10px] text-white" data-testid="save-prices-btn">
              Save Prices
            </button>
          </div>
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-white/10 bg-void/40 p-4" data-testid="weighin-settings">
            <div>
              <p className="font-mono-cap mb-1 text-[9px] text-slate-400">Weigh-in date</p>
              <input
                type="date"
                value={weighin.weighin_date}
                onChange={(e) => setWeighin((s) => ({ ...s, weighin_date: e.target.value }))}
                className="rounded-lg border border-white/15 bg-void/60 px-3 py-2 font-body text-sm text-white outline-none [color-scheme:dark]"
                data-testid="weighin-date-input"
              />
            </div>
            <div>
              <p className="font-mono-cap mb-1 text-[9px] text-slate-400">Auto-remind days before</p>
              <input
                type="number"
                min="1"
                max="30"
                value={weighin.reminder_days}
                onChange={(e) => setWeighin((s) => ({ ...s, reminder_days: e.target.value }))}
                className="w-20 rounded-lg border border-white/15 bg-void/60 px-3 py-2 font-body text-sm text-white outline-none"
                data-testid="reminder-days-input"
              />
            </div>
            <button onClick={saveSettings} className="rounded-full border border-neon-cyan/40 px-4 py-2 font-mono-cap text-[10px] hover:bg-neon-cyan/10" data-testid="save-settings-btn">
              Save Schedule
            </button>
            <button onClick={remindNow} className="rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-5 py-2 font-mono-cap text-[10px] text-white" data-testid="remind-now-btn">
              Send Reminder Now
            </button>
            {weighin.last_reminder_at && (
              <p className="w-full font-mono-cap text-[9px] text-slate-500" data-testid="last-reminder-info">
                Last sent: {new Date(weighin.last_reminder_at).toLocaleString()} · {weighin.last_reminder_sent} email(s)
              </p>
            )}
          </div>
          <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-white/10 bg-void/40 p-4" data-testid="seminar-settings">
            <div>
              <p className="font-mono-cap mb-1 text-[9px] text-slate-400">Seminar date (Adult &amp; Kids)</p>
              <input
                type="date"
                value={seminar.weighin_date}
                onChange={(e) => setSeminar((s) => ({ ...s, weighin_date: e.target.value }))}
                className="rounded-lg border border-white/15 bg-void/60 px-3 py-2 font-body text-sm text-white outline-none [color-scheme:dark]"
                data-testid="seminar-date-input"
              />
            </div>
            <div>
              <p className="font-mono-cap mb-1 text-[9px] text-slate-400">Auto-remind days before</p>
              <input
                type="number"
                min="1"
                max="30"
                value={seminar.reminder_days}
                onChange={(e) => setSeminar((s) => ({ ...s, reminder_days: e.target.value }))}
                className="w-20 rounded-lg border border-white/15 bg-void/60 px-3 py-2 font-body text-sm text-white outline-none"
                data-testid="seminar-days-input"
              />
            </div>
            <button onClick={saveSeminarSettings} className="rounded-full border border-neon-cyan/40 px-4 py-2 font-mono-cap text-[10px] hover:bg-neon-cyan/10" data-testid="save-seminar-btn">
              Save Schedule
            </button>
            <button onClick={remindSeminarNow} className="rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-5 py-2 font-mono-cap text-[10px] text-white" data-testid="remind-seminar-btn">
              Send Seminar Reminder Now
            </button>
            {seminar.last_reminder_at && (
              <p className="w-full font-mono-cap text-[9px] text-slate-500" data-testid="last-seminar-info">
                Last sent: {new Date(seminar.last_reminder_at).toLocaleString()} · {seminar.last_reminder_sent} email(s)
              </p>
            )}
          </div>
          {selectedRegs.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-violet-400/30 bg-violet-400/5 p-3" data-testid="bulk-bar">
              <span className="font-mono-cap text-[10px] text-violet-300" data-testid="bulk-count">{selectedRegs.length} selected</span>
              <select
                value={bulkField}
                onChange={(e) => { setBulkField(e.target.value); setBulkValue(""); }}
                className="rounded-lg border border-white/15 bg-void/60 px-2 py-1.5 text-xs text-white outline-none focus:border-violet-400"
                data-testid="bulk-field-select"
              >
                {BULK_FIELDS.map(([k, l]) => (
                  <option key={k} value={k} className="bg-void">{l}</option>
                ))}
              </select>
              {BULK_FIELDS.find(([k]) => k === bulkField)?.[2] !== "text" ? (
                <select
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  className="rounded-lg border border-white/15 bg-void/60 px-2 py-1.5 text-xs text-white outline-none focus:border-violet-400"
                  data-testid="bulk-value-select"
                >
                  <option value="" disabled className="bg-void">— value —</option>
                  {(BULK_OPTION_SETS[BULK_FIELDS.find(([k]) => k === bulkField)?.[2]] || []).map((o) => (
                    <option key={o} value={o} className="bg-void">{o}</option>
                  ))}
                </select>
              ) : (
                <input
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder="New value"
                  className="w-40 rounded-lg border border-white/15 bg-void/60 px-2 py-1.5 text-xs text-white outline-none focus:border-violet-400"
                  data-testid="bulk-value-input"
                />
              )}
              <button onClick={applyBulk} disabled={bulkBusy} className="rounded-md bg-violet-400 px-3 py-1.5 font-mono-cap text-[9px] text-void transition-colors hover:bg-neon-pink disabled:opacity-50" data-testid="bulk-apply-btn">
                {bulkBusy ? "Applying…" : "Apply to Selected"}
              </button>
              <button onClick={() => setSelectedRegs([])} className="font-mono-cap text-[9px] text-slate-400 underline underline-offset-2" data-testid="bulk-clear-btn">
                Clear
              </button>
            </div>
          )}
          {pendingRegs === null && regs.length === 0 ? (
            <p className="font-body text-sm text-slate-500">No registrations yet.</p>
          ) : shownRegs.length === 0 ? (
            <p className="font-body text-sm text-slate-500">
              {pendingRegs !== null ? "No unpaid/abandoned checkouts." : `No registrations match "${regQuery}".`}
            </p>
          ) : (
            <div className="space-y-3">
              {shownRegs.map((r) => (
                <div key={r.id} className="flex flex-col gap-4 rounded-xl border border-white/10 bg-void/40 p-4 sm:flex-row" data-testid={`reg-${r.id}`}>
                  <div className="group relative h-28 w-28 shrink-0">
                    {r.photo_path ? (
                      <img
                        src={`${API}/api/files/${r.photo_path}?auth=${token}`}
                        alt={r.full_name}
                        className="h-28 w-28 rounded-xl border border-white/10 object-cover"
                        data-testid={`reg-photo-${r.id}`}
                      />
                    ) : (
                      <div className="flex h-28 w-28 items-center justify-center rounded-xl border border-dashed border-white/20 text-slate-600">
                        <User className="h-6 w-6" />
                      </div>
                    )}
                    <button
                      onClick={() => pickRegPhoto(r.id)}
                      disabled={photoBusy === r.id}
                      className="absolute inset-x-1 bottom-1 rounded-md bg-black/70 px-1 py-1 font-mono-cap text-[8px] text-white transition-colors hover:bg-neon-pink disabled:opacity-60 sm:opacity-0 sm:group-hover:opacity-100"
                      data-testid={`reg-photo-edit-${r.id}`}
                    >
                      {photoBusy === r.id ? "Uploading…" : r.photo_path ? "Replace Photo" : "Add Photo"}
                    </button>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedRegs.includes(r.id)}
                        onChange={() => toggleRegSelect(r.id)}
                        className="h-4 w-4 shrink-0 cursor-pointer accent-violet-400"
                        title="Select for bulk edit"
                        data-testid={`reg-select-${r.id}`}
                      />
                      <span className={`rounded-full px-3 py-1 font-mono-cap text-[9px] ${
                        r.status === "paid" ? "bg-emerald-500/20 text-emerald-300"
                        : r.status === "pending_review" ? "bg-sky-500/20 text-sky-300"
                        : "bg-amber-500/20 text-amber-300"
                      }`}>{r.status === "pending_review" ? "awaiting approval" : r.status}</span>
                      {r.status === "pending_review" && (
                        <button onClick={() => approvePayment(r)} className="rounded-md border border-emerald-400/40 px-2 py-0.5 font-mono-cap text-[9px] text-emerald-300 transition-colors hover:bg-emerald-400/10" data-testid={`reg-approve-${r.id}`}>
                          Approve Payment
                        </button>
                      )}
                      {(r.status === "pending" || r.status === "expired") && (
                        <button onClick={() => syncPayment(r)} className="rounded-md border border-amber-400/40 px-2 py-0.5 font-mono-cap text-[9px] text-amber-300 transition-colors hover:bg-amber-400/10" data-testid={`reg-sync-${r.id}`}>
                          Sync Payment
                        </button>
                      )}
                      {(r.status === "pending" || r.status === "expired") && (
                        <button onClick={() => forceApprove(r)} className="rounded-md border border-red-400/40 px-2 py-0.5 font-mono-cap text-[9px] text-red-300 transition-colors hover:bg-red-400/10" data-testid={`reg-force-approve-${r.id}`}>
                          Force Approve
                        </button>
                      )}
                      {(r.status === "pending" || r.status === "expired") && (
                        <button onClick={() => copyPaymentLink(r)} className="rounded-md border border-sky-400/40 px-2 py-0.5 font-mono-cap text-[9px] text-sky-300 transition-colors hover:bg-sky-400/10" data-testid={`reg-paylink-${r.id}`}>
                          Copy Pay Link
                        </button>
                      )}
                      {(r.status === "pending_review" || r.status === "paid") && (
                        <button onClick={() => resendEmail(r)} className="rounded-md border border-sky-400/40 px-2 py-0.5 font-mono-cap text-[9px] text-sky-300 transition-colors hover:bg-sky-400/10" data-testid={`reg-resend-${r.id}`}>
                          Resend Email
                        </button>
                      )}
                      {(r.status === "pending_review" || r.status === "paid") && (
                        <button onClick={() => setEmailEdit(emailEdit?.id === r.id ? null : { id: r.id, value: r.email || "" })} className="rounded-md border border-amber-400/40 px-2 py-0.5 font-mono-cap text-[9px] text-amber-300 transition-colors hover:bg-amber-400/10" data-testid={`reg-fix-email-${r.id}`}>
                          Fix Email
                        </button>
                      )}
                      <button onClick={() => openDetailsEdit(r)} className="rounded-md border border-violet-400/40 px-2 py-0.5 font-mono-cap text-[9px] text-violet-300 transition-colors hover:bg-violet-400/10" data-testid={`reg-edit-details-${r.id}`}>
                        Edit Details
                      </button>
                      {r.details_history?.length > 0 && (
                        <button onClick={() => setHistoryOpen(historyOpen === r.id ? null : r.id)} className="rounded-md border border-white/20 px-2 py-0.5 font-mono-cap text-[9px] text-slate-300 transition-colors hover:bg-white/10" data-testid={`reg-history-${r.id}`}>
                          History ({r.details_history.length})
                        </button>
                      )}
                      {r.reg_no && <span className="rounded-md bg-neon-cyan/10 px-2 py-0.5 font-mono text-[10px] tracking-wider text-neon-cyan" data-testid={`reg-no-${r.id}`}>{r.reg_no}</span>}
                      <span className="rounded-md bg-violet-500/15 px-2 py-0.5 font-mono-cap text-[9px] text-violet-300">{r.reg_type === "seminar_adult" ? "Seminar Adult" : r.reg_type === "seminar_kids" ? "Seminar Kids" : "Amateur"}</span>
                      {(() => {
                        const rec = r.status === "pending_review" ? r.email_awaiting : r.status === "paid" ? r.email_confirmation : null;
                        if (!rec || !rec.status) return null;
                        const ok = rec.status === "sent";
                        const when = rec.at ? new Date(rec.at).toLocaleString("en-MY", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
                        return (
                          <span
                            title={`${ok ? "Sent" : "FAILED"} to ${rec.to || "client email"}${when ? ` · ${when}` : ""}`}
                            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono-cap text-[9px] ${ok ? "border-emerald-400/40 text-emerald-300" : "border-red-400/50 text-red-300"}`}
                            data-testid={`reg-email-badge-${r.id}`}
                          >
                            {ok ? <MailCheck className="h-3 w-3" /> : <MailX className="h-3 w-3" />}
                            {ok ? "Email sent" : "Email failed"}
                          </span>
                        );
                      })()}
                      <button
                        onClick={() => deleteRegistration(r)}
                        title="Delete registration"
                        className="ml-auto rounded-full border border-white/15 p-1.5 text-slate-400 transition-colors hover:border-neon-pink hover:text-neon-pink"
                        data-testid={`reg-delete-${r.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2" data-testid={`reg-name-line-${r.id}`}>
                      <p className="font-sub text-sm font-bold uppercase text-white">{r.full_name}</p>
                      {r.weight_class && <span className="font-mono-cap text-[9px] text-neon-pink">{r.weight_class} · {r.age_category}</span>}
                    </div>
                    {emailEdit?.id === r.id && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-sky-400/30 bg-sky-400/5 p-2" data-testid={`reg-email-edit-${r.id}`}>
                        <input
                          value={emailEdit.value}
                          onChange={(e) => setEmailEdit({ id: r.id, value: e.target.value })}
                          placeholder="Correct client email"
                          className="w-64 rounded-lg border border-white/15 bg-void/60 px-3 py-1.5 text-xs text-white outline-none focus:border-sky-400"
                          data-testid={`reg-email-input-${r.id}`}
                        />
                        <button onClick={() => saveEmail(r)} className="rounded-md bg-sky-400 px-3 py-1.5 font-mono-cap text-[9px] text-void transition-colors hover:bg-neon-cyan" data-testid={`reg-email-save-${r.id}`}>
                          Save & Resend
                        </button>
                        <button onClick={() => setEmailEdit(null)} className="font-mono-cap text-[9px] text-slate-400 underline underline-offset-2" data-testid={`reg-email-cancel-${r.id}`}>
                          Cancel
                        </button>
                      </div>
                    )}
                    {detailsEdit?.id === r.id && (
                      <div className="mt-2 rounded-lg border border-violet-400/30 bg-violet-400/5 p-3" data-testid={`reg-details-panel-${r.id}`}>
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                          {(REG_DETAIL_FIELDS[r.reg_type] || REG_DETAIL_FIELDS.amateur).map(([key, label, opt]) => (
                            <label key={key} className="block">
                              <span className="font-mono-cap mb-1 block text-[8px] text-slate-400">{label}</span>
                              {opt && opt !== "number" && opt !== "date" ? (
                                <select
                                  value={detailsEdit.form[key] || ""}
                                  onChange={(e) => setDetailsEdit({ id: r.id, form: { ...detailsEdit.form, [key]: e.target.value } })}
                                  className="w-full rounded-lg border border-white/15 bg-void/60 px-2 py-1.5 text-xs text-white outline-none focus:border-violet-400"
                                  data-testid={`reg-details-${key}-${r.id}`}
                                >
                                  <option value="" disabled className="bg-void">— select —</option>
                                  {REG_DETAIL_OPTIONS[opt].map((o) => (
                                    <option key={o} value={o} className="bg-void">{o}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type={opt === "number" ? "number" : opt === "date" ? "date" : "text"}
                                  value={detailsEdit.form[key] || ""}
                                  onChange={(e) => setDetailsEdit({ id: r.id, form: { ...detailsEdit.form, [key]: e.target.value } })}
                                  className="w-full rounded-lg border border-white/15 bg-void/60 px-2 py-1.5 text-xs text-white outline-none focus:border-violet-400 [color-scheme:dark]"
                                  data-testid={`reg-details-${key}-${r.id}`}
                                />
                              )}
                            </label>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <button onClick={() => saveDetails(r)} className="rounded-md bg-violet-400 px-3 py-1.5 font-mono-cap text-[9px] text-void transition-colors hover:bg-neon-pink" data-testid={`reg-details-save-${r.id}`}>
                            Save Changes
                          </button>
                          <button onClick={() => setDetailsEdit(null)} className="font-mono-cap text-[9px] text-slate-400 underline underline-offset-2" data-testid={`reg-details-cancel-${r.id}`}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                    {historyOpen === r.id && r.details_history?.length > 0 && (
                      <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`reg-history-panel-${r.id}`}>
                        <p className="font-mono-cap mb-2 text-[9px] text-slate-400">Edit History</p>
                        <div className="space-y-2">
                          {[...r.details_history].reverse().map((h, i) => (
                            <div key={i} className="rounded-lg bg-white/5 p-2" data-testid={`reg-history-entry-${r.id}-${i}`}>
                              <p className="font-mono-cap text-[8px] text-slate-500">
                                {new Date(h.at).toLocaleString()}{h.bulk ? " · bulk edit" : ""}{h.by ? ` · by ${h.by}` : ""}
                                {h.email_notified ? ` · email ${h.email_notified}` : ""}
                              </p>
                              {Object.entries(h.changes || {}).map(([f, c]) => (
                                <p key={f} className="font-body text-xs text-slate-300">
                                  <span className="text-slate-500">{REG_FIELD_LABELS[f] || f}:</span>{" "}
                                  <span className="text-slate-500 line-through">{c.from ?? "—"}</span>{" "}
                                  → <span className="text-neon-cyan">{c.to ?? "—"}</span>
                                </p>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-2 grid gap-x-6 gap-y-1 font-body text-xs text-slate-400 sm:grid-cols-2">
                      <p>NRIC/Passport: <span className="text-slate-300">{r.nric_passport}</span></p>
                      <p>{r.gender}{r.nationality ? ` · ${r.nationality}` : ""}</p>
                      <p>{r.height_cm ? `Height: ${r.height_cm} CM · ` : ""}Jersey: {r.jersey_size}</p>
                      {r.club_gym && <p>Club/Gym: <span className="text-slate-300">{r.club_gym}</span></p>}
                      {r.coach_name && <p>Coach: {r.coach_name} ({r.coach_phone})</p>}
                      {r.contact_number && <p>Contact: <span className="text-slate-300">{r.contact_number}</span></p>}
                      {r.date_of_birth && <p>DOB: {r.date_of_birth} · Age: {r.age_category}</p>}
                      {r.guardian_name && <p>Parent/Guardian: <span className="text-slate-300">{r.guardian_name}</span></p>}
                      {r.fight_record && <p>Record: {r.fight_record}</p>}
                      <p className="break-all sm:col-span-2">{r.email} · MYR {r.amount}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {summary && (
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-summary">
            <div className="rounded-2xl border border-white/10 bg-surface/80 p-5">
              <p className="font-mono-cap text-[9px] text-neon-cyan">Revenue (paid)</p>
              <p className="mt-1 font-display text-2xl gradient-text">MYR {summary.revenue.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-surface/80 p-5">
              <p className="font-mono-cap text-[9px] text-neon-cyan">Paid Orders</p>
              <p className="mt-1 font-display text-2xl text-white">{summary.orders}</p>
            </div>
            <div className="col-span-2 rounded-2xl border border-white/10 bg-surface/80 p-5">
              <p className="font-mono-cap text-[9px] text-neon-cyan">Units Sold</p>
              {Object.keys(summary.units).length === 0 ? (
                <p className="mt-1 font-body text-sm text-slate-500">None yet</p>
              ) : (
                <div className="mt-1 space-y-0.5">
                  {Object.entries(summary.units).map(([n, u]) => (
                    <p key={n} className="font-body text-xs text-slate-300">{u}× {n}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-white/10 bg-surface/80 p-5" data-testid="admin-products">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-mono-cap text-[11px] text-neon-cyan">Products — edit, show/hide, price &amp; stock</p>
              <button onClick={addProduct} className="rounded-full border border-neon-cyan/40 px-4 py-1.5 font-mono-cap text-[10px] hover:bg-neon-cyan/10" data-testid="add-product-btn">+ Add product</button>
            </div>
            <div className="space-y-4">
              {products.map((p) => (
                <div key={p.id} className="rounded-xl border border-white/10 bg-void/40 p-4" data-testid={`product-${p.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 font-mono-cap text-[10px] text-slate-300">
                      <input type="checkbox" checked={!!p.published} onChange={(e) => patchProduct(p.id, "published", e.target.checked)} data-testid={`pub-${p.id}`} />
                      {p.published ? "Shown" : "Hidden"}
                    </label>
                    <label className="flex items-center gap-2 font-mono-cap text-[10px] text-slate-300" title="Stock numbers are always enforced at checkout when filled in. This toggle also controls stock display and auto-restock on order deletion.">
                      <input type="checkbox" checked={!!p.track} onChange={(e) => patchProduct(p.id, "track", e.target.checked)} data-testid={`track-${p.id}`} />
                      {p.track ? "Stock tracked" : "Not tracked"}
                    </label>
                    <input value={p.name} onChange={(e) => patchProduct(p.id, "name", e.target.value)} className="min-w-[180px] flex-1 rounded-lg border border-white/15 bg-void/60 px-3 py-2 text-sm text-white outline-none" />
                    <div className="flex items-center gap-1"><span className="font-mono-cap text-[9px] text-slate-500">RM</span>
                      <input type="number" value={p.price_value} onChange={(e) => patchProduct(p.id, "price_value", e.target.value)} className="w-20 rounded-lg border border-white/15 bg-void/60 px-2 py-2 text-sm text-white outline-none" /></div>
                  </div>
                  <input value={p.image || ""} onChange={(e) => patchProduct(p.id, "image", e.target.value)} placeholder="Image URL" className="mt-2 w-full rounded-lg border border-white/15 bg-void/60 px-3 py-2 text-xs text-slate-300 outline-none" />
                  <textarea
                    value={(p.images || []).join("\n")}
                    onChange={(e) => patchProduct(p.id, "images", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                    placeholder="Extra photo URLs for the popup gallery (one per line)"
                    rows={2}
                    className="mt-2 w-full rounded-lg border border-white/15 bg-void/60 px-3 py-2 text-xs text-slate-300 outline-none"
                    data-testid={`images-${p.id}`}
                  />
                  {(p.sizes || []).length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {p.sizes.map((sz) => (
                        <div key={sz} className={`flex items-center gap-1 rounded-lg border px-2 py-1 ${Number((p.stock || {})[sz] ?? 0) <= 3 ? "border-neon-pink/50" : "border-white/15"}`}>
                          <span className="font-mono-cap text-[9px] text-slate-400">{sz}</span>
                          <input type="number" value={(p.stock || {})[sz] ?? 0} onChange={(e) => patchStock(p.id, sz, e.target.value)} className="w-12 rounded bg-void/60 px-1 text-center text-sm text-white outline-none" data-testid={`stock-${p.id}-${sz}`} />
                        </div>
                      ))}
                    </div>
                  ) : p.track ? (
                    <div className="mt-2">
                      <div className={`flex w-32 items-center gap-1 rounded-lg border px-2 py-1 ${Number((p.stock || {}).ALL ?? 0) <= 3 ? "border-neon-pink/50" : "border-white/15"}`}>
                        <span className="font-mono-cap text-[9px] text-slate-400">STOCK</span>
                        <input type="number" value={(p.stock || {}).ALL ?? 0} onChange={(e) => patchStock(p.id, "ALL", e.target.value)} className="w-14 rounded bg-void/60 px-1 text-center text-sm text-white outline-none" data-testid={`stock-${p.id}-ALL`} />
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => saveProduct(p)} className="rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-5 py-2 font-mono-cap text-[10px] text-white" data-testid={`save-${p.id}`}>Save</button>
                    <button onClick={() => delProduct(p.id)} className="rounded-full border border-white/15 px-4 py-2 font-mono-cap text-[10px] text-slate-400 hover:border-neon-pink hover:text-neon-pink">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, order id, tracking…"
            className="w-full rounded-full border border-white/15 bg-void/60 px-5 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan sm:max-w-md"
            data-testid="admin-search"
          />
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`rounded-full border px-4 py-2 font-mono-cap text-[10px] capitalize ${statusFilter === f ? "border-neon-pink bg-neon-pink/10 text-white" : "border-white/15 text-slate-300"}`}
                data-testid={`admin-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {filtered.map((o) => (
            <div key={o.order_id} className="rounded-2xl border border-white/10 bg-surface/80 p-5" data-testid={`admin-order-${o.order_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`rounded-full px-3 py-1 font-mono-cap text-[9px] ${o.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : o.status === "shipped" ? "bg-sky-500/20 text-sky-300" : "bg-amber-500/20 text-amber-300"}`}>{o.status}</span>
                <div className="flex items-center gap-2">
                  <span className="font-display text-xl gradient-text">MYR {o.amount}</span>
                  <button
                    onClick={() => deleteOrder(o)}
                    title="Delete order"
                    className="rounded-full border border-white/15 p-2 text-slate-400 transition-colors hover:border-neon-pink hover:text-neon-pink"
                    data-testid={`order-delete-${o.order_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="mt-2 font-mono text-[10px] text-slate-500 break-all">{o.order_id}</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="font-sub text-sm font-bold text-white">{o.customer_name}</p>
                  <p className="font-body text-xs text-slate-400">{o.customer_email} · {o.phone}</p>
                  <p className="font-body text-xs text-slate-400">{o.address}, {o.postcode} {o.state} ({o.region})</p>
                </div>
                <div className="text-xs text-slate-300">
                  {(o.items || []).map((it, i) => (
                    <p key={i}>{it.quantity}× {it.name}{it.size ? ` (${it.size})` : ""} — MYR {it.unit_price_myr}</p>
                  ))}
                  <p className="mt-1 text-slate-500">+ postage MYR {o.postage}</p>
                </div>
              </div>

              {o.status === "shipped" ? (
                <p className="mt-4 rounded-xl border border-sky-500/30 bg-sky-500/5 px-4 py-2 font-mono-cap text-[10px] text-sky-300">
                  Shipped · Ninja Van {o.tracking_number}
                </p>
              ) : o.status === "paid" ? (
                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <input
                    value={tracking[o.order_id] || ""}
                    onChange={(e) => setTracking((t) => ({ ...t, [o.order_id]: e.target.value }))}
                    placeholder="Ninja Van tracking number"
                    className="flex-1 rounded-full border border-white/15 bg-void/60 px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan"
                    data-testid={`ship-input-${o.order_id}`}
                  />
                  <button
                    onClick={() => ship(o.order_id)}
                    className="rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-6 py-2.5 font-mono-cap text-[10px] text-white"
                    data-testid={`ship-btn-${o.order_id}`}
                  >
                    Mark Shipped + Email
                  </button>
                </div>
              ) : null}
            </div>
          ))}
          {filtered.length === 0 && <p className="text-center font-body text-slate-500">No orders match.</p>}
        </div>
      </div>
    </div>
  );
}
